// What the presenter and the owners did in this browser: the decision on each
// opportunity and the answers to the tasks. Kept in localStorage so the
// owner's answer, given in another tab from a Slack link, shows on the
// project page; cleared with `resetUsageDemo`.

import { useEffect, useState } from "react";
import type { Answer, Decision, Fulfilment, Intake } from "./types";

export interface UsageDemoState {
    decisions: Record<string, Decision>;
    /** The decisions before the current one, oldest first, so Undo steps back. */
    history: Record<string, Decision[]>;
    /** How the intake routed each need, per project whose negotiation started. */
    intakes: Record<string, Intake>;
    /** The delegates' answers, by need. */
    answers: Record<string, Answer>;
}

const EMPTY: UsageDemoState = { decisions: {}, history: {}, intakes: {}, answers: {} };
// Versioned: state written by an earlier shape of the demo is ignored.
const KEY = "demo.usage.v2";
const EVENT = "demo.usage.change";

const KINDS: Decision["kind"][] = [
    "requested",
    "submitted",
    "delivered",
    "revision",
    "proposal",
    "signed",
    "cancelled",
    "renew",
    "snoozed",
    "dismissed",
];
const known = (d: Decision | undefined): d is Decision => Boolean(d && KINDS.includes(d.kind));

const read = (): UsageDemoState => {
    try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) return EMPTY;
        const parsed = JSON.parse(raw) as Partial<UsageDemoState>;
        const intakes = Object.fromEntries(
            Object.entries(parsed.intakes ?? {}).filter(
                ([, intake]) => intake && typeof intake.fulfilments === "object"
            )
        );
        // A decision of a kind the demo no longer has (left by an earlier run) is dropped;
        // the project falls back to its latest known step.
        const history = Object.fromEntries(
            Object.entries(parsed.history ?? {}).map(([id, past]) => [
                id,
                (past ?? []).filter(known),
            ])
        );
        const decisions: Record<string, Decision> = {};
        for (const [id, d] of Object.entries(parsed.decisions ?? {})) {
            if (known(d)) decisions[id] = d;
            else if (history[id]?.length) decisions[id] = history[id].pop()!;
        }
        return {
            decisions,
            history,
            intakes,
            answers: parsed.answers ?? {},
        };
    } catch {
        return EMPTY;
    }
};

const write = (next: UsageDemoState) => {
    window.localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(EVENT));
};

const update = (fn: (s: UsageDemoState) => UsageDemoState) => write(fn(read()));

export const useUsageDemoState = (): UsageDemoState => {
    const [state, setState] = useState<UsageDemoState>(read);
    useEffect(() => {
        const onChange = () => setState(read());
        window.addEventListener(EVENT, onChange);
        window.addEventListener("storage", onChange);
        return () => {
            window.removeEventListener(EVENT, onChange);
            window.removeEventListener("storage", onChange);
        };
    }, []);
    return state;
};

/** The project moves to a new decision; the one before is kept for Undo. */
const advance = (s: UsageDemoState, projectId: string, decision: Decision): UsageDemoState => {
    const current = s.decisions[projectId];
    return {
        ...s,
        decisions: { ...s.decisions, [projectId]: decision },
        history: {
            ...s.history,
            [projectId]: current
                ? [...(s.history[projectId] ?? []), current]
                : (s.history[projectId] ?? []),
        },
    };
};

export const decide = (projectId: string, decision: Decision) =>
    update((s) => advance(s, projectId, decision));

/**
 * One step back for the presenter: the decision before the current one. Back
 * past the start of the negotiation, the intake and the answers go too.
 */
export const undoDecision = (projectId: string) =>
    update((s) => {
        const past = s.history[projectId] ?? [];
        const previous = past[past.length - 1];
        const next: UsageDemoState = {
            ...s,
            decisions: { ...s.decisions },
            history: { ...s.history, [projectId]: past.slice(0, -1) },
        };
        if (previous) next.decisions[projectId] = previous;
        else delete next.decisions[projectId];
        if (
            !previous ||
            previous.kind === "snoozed" ||
            previous.kind === "renew" ||
            previous.kind === "dismissed"
        ) {
            const { [projectId]: intake, ...intakes } = s.intakes;
            const answers = { ...s.answers };
            for (const needId of Object.keys(intake?.fulfilments ?? {})) delete answers[needId];
            return { ...next, intakes, answers };
        }
        return next;
    });

/** The intake is done: the negotiation starts and the routed needs become asks. */
export const completeIntake = (projectId: string, intake: Intake) =>
    update((s) => ({
        ...advance(s, projectId, { kind: "requested", on: intake.on }),
        intakes: { ...s.intakes, [projectId]: intake },
    }));

/** An open ask takes another route: handed to someone else, skipped, or answered by the user. */
export const reroute = (projectId: string, needId: string, how: Fulfilment) =>
    update((s) => {
        const intake = s.intakes[projectId];
        if (!intake) return s;
        return {
            ...s,
            intakes: {
                ...s.intakes,
                [projectId]: { ...intake, fulfilments: { ...intake.fulfilments, [needId]: how } },
            },
        };
    });

/** Every ask fulfilled: the project goes to InfinityLoop for the deliverable. */
export const submit = (projectId: string, on: string) =>
    update((s) => advance(s, projectId, { kind: "submitted", on }));

export const answer = (needId: string, value: Answer) =>
    update((s) => ({ ...s, answers: { ...s.answers, [needId]: value } }));

export const resetUsageDemo = () => write(EMPTY);

/** One scenario back to its start: the project's decisions, intake and answers go; the other projects keep theirs. */
export const resetUsageProject = (projectId: string, needIds: string[]) =>
    update((s) => {
        const { [projectId]: _d, ...decisions } = s.decisions;
        const { [projectId]: _h, ...history } = s.history;
        const { [projectId]: _i, ...intakes } = s.intakes;
        const answers = { ...s.answers };
        for (const id of needIds) delete answers[id];
        return { decisions, history, intakes, answers };
    });

/** The deliverable landed (simulated by the presenter). */
export const deliver = (projectId: string, on: string) =>
    update((s) => advance(s, projectId, { kind: "delivered", on }));

/** The user pushed back on the deliverable. */
export const requestUpdate = (projectId: string, comment: string, on: string) =>
    update((s) => advance(s, projectId, { kind: "revision", comment, on }));

/** The vendor answered; the user recorded it. */
export const updateProposal = (projectId: string, note: string, on: string) =>
    update((s) => advance(s, projectId, { kind: "proposal", note, on }));

/** The deal is signed: the negotiation closes on the signed agreement. */
export const sign = (projectId: string, document: string, on: string) =>
    update((s) => advance(s, projectId, { kind: "signed", document, on }));
